import { BALANCE } from '../config/balance';
import { STEP } from '../core/loop';
import { SpatialHash } from '../core/spatial-hash';
import type {
  DamageSource,
  GameEvent,
  GamePhase,
  InputState,
  OfferId,
  RunMode,
  WeaponId,
} from '../core/types';
import type { StreamBundle } from '../core/rng';
import { Player } from './player';
import { ObjectPool } from './pool';
import {
  createEnemy, updateEnemy, type Enemy,
} from './enemies';
import {
  spawnEnemy as spawnEnemyFn,
  spawnBoss as spawnBossFn,
  fireSpit as fireSpitFn,
  fireBossBolt as fireBossBoltFn,
  summonMinions as summonMinionsFn,
  dropLoot,
} from './spawners';
import {
  createGem, updateGem, type Gem,
} from './gems';
import { createCoin, updateCoin, type Coin } from './coins';
import {
  createBolt, type Bolt,
} from './bolts';
import {
  createFoeBolt,
  type FoeBolt,
} from './foe-bolts';
import {
  createSwipe, type Swipe,
} from './swipes';
import { createParticle, type Particle } from './particles';
import { WaveDirector } from './timeline-director';
import { DeathLog, type DamageRecord, type DeathResult } from './death-cause';
import {
  drawOffers,
  initialBuild,
  readyEvolutions,
  recomputeStats,
} from './build';
import { WeaponSet } from './weapon-set';
import {
  enemyContact, foeBoltHits, weaponHits,
} from './collisions';
import { Fx } from './fx';
import { DamageNumbers } from './damage-numbers';
import { computeScore, type ScoreBreakdown } from './score';
import { variantForDate, type Variant } from './variants';
import {
  updateFoeBolts as updateFoeBoltsFn,
  updateSwipes as updateSwipesFn,
  updateParticles as updateParticlesFn,
} from './updaters';
import type { EvolutionId, EnemyKind, PassiveId } from '../core/types';

export class Engine {
  phase: GamePhase = 'playing';
  time = 0;
  kills = 0;
  combo = 0;
  freezeMs = 0;

  player = new Player();
  build = initialBuild();
  weaponSet = new WeaponSet();

  enemyPool = new ObjectPool<Enemy>(createEnemy, BALANCE.pools.enemies);
  gemPool = new ObjectPool<Gem>(createGem, BALANCE.pools.gems);
  particlePool = new ObjectPool<Particle>(createParticle, BALANCE.pools.particles);
  boltPool = new ObjectPool<Bolt>(createBolt, BALANCE.pools.bolts);
  foeBoltPool = new ObjectPool<FoeBolt>(createFoeBolt, BALANCE.pools.foeBolts);
  swipePool = new ObjectPool<Swipe>(createSwipe, BALANCE.pools.swipes);
  coinPool = new ObjectPool<Coin>(createCoin, BALANCE.pools.coins);

  enemyHash = new SpatialHash<Enemy>(96);
  hashScratch: Enemy[] = [];

  fx!: Fx; streams!: StreamBundle; mode!: RunMode;
  seed!: number; variant!: Variant; date!: string;
  damageNumbers = new DamageNumbers();

  offers: OfferId[] = [];
  death: DeathResult | null = null;
  scoreBreakdown: ScoreBreakdown | null = null;
  events: GameEvent[] = [];
  spawnLog: string[] = [];
  gold = 0;
  faceX = 1;
  faceY = 0;
  anchors = 0;
  boss1KilledAt: number | null = null;
  boss2KilledAt: number | null = null;
  /** 服务端无头 AI 模拟：跳过纯视觉更新，同屏上限下调。 */
  headless: boolean;

  private director: WaveDirector;
  private deathLog = new DeathLog();
  private comboTimer = 0;
  private snapshotTimer = 0;
  private pendingLevels = 0;
  private lethal: DamageRecord | null = null;

  constructor(
    streams: StreamBundle, mode: RunMode, seed: number, date: string,
    startWeapon: WeaponId = 'boomerang', headless = false,
  ) {
    this.build = initialBuild(startWeapon);
    this.streams = streams;
    this.mode = mode;
    this.seed = seed;
    this.date = date;
    this.variant = variantForDate(date);
    this.headless = headless;
    this.fx = new Fx(this.particlePool, streams.fx);
    this.fx.enabled = !headless;
    this.director = new WaveDirector(mode);
    this.refreshStats();
    this.player.hp = this.player.stats.maxHp;
    this.weaponSet.syncBuild(this.build);
  }
  /** 重算面板并套用当日变体（移速 / 血量乘区）。 */
  refreshStats(): void {
    const s = recomputeStats(this.build);
    s.maxHp *= this.variant.playerMaxHpMul;
    s.speed *= this.variant.playerSpeedMul;
    this.player.stats = s;
  }

  emit(ev: GameEvent): void {
    this.events.push(ev);
  }

  addFreeze(seconds: number): void {
    this.freezeMs = Math.max(this.freezeMs, seconds * 100);
  }
  // —— 导演 / Boss 回调（实现见 spawners.ts）——

  spawnEnemy(kind: EnemyKind, x: number, y: number): void {
    spawnEnemyFn(this, kind, x, y);
  }

  spawnBoss(kind: EnemyKind): void {
    spawnBossFn(this, kind);
  }

  fireSpit(e: Enemy): void {
    fireSpitFn(this, e);
  }

  fireBossBolt(angle: number, speed: number, e: Enemy, skill: string): void {
    fireBossBoltFn(this, angle, speed, e, skill);
  }

  summonMinions(e: Enemy, n: number): void {
    summonMinionsFn(this, e, n);
  }
  // —— 主循环 ——

  step(input: InputState): void {
    if (this.phase !== 'playing') return;
    const dt = STEP;
    this.time += dt;

    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.combo = 0;
    }
    if (Math.hypot(input.moveX, input.moveY) > 0.1) {
      this.faceX = input.moveX; this.faceY = input.moveY;
    }

    const p = this.player;
    p.update(input, dt);
    this.weaponSet.update(this, dt);
    for (const e of this.enemyPool.all()) {
      if (e.active) updateEnemy(e, dt, this);
    }
    updateFoeBoltsFn(this, dt);
    updateSwipesFn(this, dt);
    this.detonateFused();
    this.director.tick(this, dt);
    this.updateGems(dt);
    this.updateCoins(dt);
    this.enemyHash.clear();
    for (const e of this.enemyPool.all()) if (e.active) this.enemyHash.insert(e.x, e.y, e);

    weaponHits(this);
    foeBoltHits(this);
    enemyContact(this);
    if (!this.headless) { updateParticlesFn(this, dt); this.damageNumbers.update(dt); }
    this.takeSnapshot(dt, input);

    this.anchors = Math.floor(this.time / 60);
    if (this.pendingLevels > 0) this.openLevelUp();
    if (p.hp <= 0 && this.phase === 'playing') this.die();
    const wave = BALANCE.waves[this.mode === 'race' ? 'race' : 'lesson'];
    if (this.mode !== 'practice' && this.time >= wave.duration) this.complete();
  }

  pause(): void {
    if (this.mode === 'practice' && this.phase === 'playing') this.phase = 'paused';
  }
  resume(): void { if (this.phase === 'paused') this.phase = 'playing'; }


  private complete(): void {
    this.phase = 'complete';
    this.scoreBreakdown = computeScore(this, this.mode);
    this.emit({ type: 'death', x: this.player.x, y: this.player.y });
  }

  // —— 击杀 / 掉落 ——

  registerKill(e: Enemy): void {
    this.kills++;
    this.combo++;
    this.comboTimer = BALANCE.juice.comboWindow;
    dropLoot(this, e);
    this.handleBossDeath(e);
    this.fx.killBurst(e);
    this.addFreeze(BALANCE.juice.hitstopKill);
    this.emit({ type: 'kill', x: e.x, y: e.y, combo: this.combo });
    this.enemyPool.release(e);
  }

  private handleBossDeath(e: Enemy): void {
    if (e.kind === 'boss1') {
      this.boss1KilledAt = this.time;
      if (this.mode !== 'practice') this.complete();
    }
    if (e.kind === 'boss2') {
      this.boss2KilledAt = this.time; this.complete();
    }
  }

  // —— 受击 ——

  hurtPlayer(amount: number, source: DamageSource): void {
    const p = this.player;
    if (p.iframes > 0) return;

    if (p.stats.shield) {
      this.build.passive.shield = 0; this.refreshStats();
      p.shieldBreakAt = this.time;
      p.iframes = BALANCE.player.iframes;
      this.deathLog.recordShieldBreak(this.time);
      this.emit({ type: 'shield_break', x: p.x, y: p.y });
      return;
    }

    p.hp -= amount;
    p.iframes = BALANCE.player.iframes;
    const rec: DamageRecord = {
      t: this.time, source, amount, hpRatio: Math.max(0, p.hpRatio),
    };
    this.lethal = rec;
    this.deathLog.recordDamage(rec.t, rec.source, rec.amount, rec.hpRatio);
    this.emit({ type: 'damage', x: p.x, y: p.y, amount, source, hpRatio: rec.hpRatio });
    this.addFreeze(BALANCE.juice.hitstopHurt);
  }

  // —— 三选一 / reroll / 进化 ——

  chooseUpgrade(id: OfferId): void {
    if (this.phase !== 'levelup' || !this.offers.includes(id)) return;
    if (this.isWeapon(id)) this.build.weapon[id as WeaponId]++;
    else this.build.passive[id as PassiveId]++;
    this.refreshStats();
    this.weaponSet.syncBuild(this.build);
    this.phase = 'playing';
    if (this.pendingLevels > 0) this.openLevelUp();
  }

  reroll(): boolean {
    const cost = this.variant.rerollCost;
    if (this.gold < cost) return false;
    this.gold -= cost;
    this.offers = drawOffers(this.streams.offers, this.build, this.offers);
    return true;
  }

  private isWeapon(id: OfferId): boolean {
    return ['boomerang', 'staff', 'whip', 'arrow'].includes(id);
  }

  private applyEvolution(id: EvolutionId): void {
    this.build.evolved[id] = true;
    this.refreshStats();
    this.weaponSet.syncBuild(this.build);
    this.emit({ type: 'evolve', x: this.player.x, y: this.player.y, text: id });
  }

  private pickCore(): void {
    const ready = readyEvolutions(this.build);
    if (ready[0]) this.applyEvolution(ready[0]);
    else this.gold += BALANCE.score.coreGold;
  }

  // —— 实体更新杂项 ——

  private detonateFused(): void {
    for (const e of this.enemyPool.all()) {
      if (e.active && e.kind === 'bomber'
        && e.state === 'fuse'
        && e.stateT >= BALANCE.enemies.bomber.fuseTime) this.explode(e);
    }
  }

  private explode(e: Enemy): void {
    const c = BALANCE.enemies.bomber;
    this.fx.explosion(e.x, e.y, c.explodeRadius);
    this.addFreeze(BALANCE.juice.hitstopExplosion);
    this.emit({ type: 'explosion', x: e.x, y: e.y, amount: e.damage });
    const p = this.player;
    if (Math.hypot(p.x - e.x, p.y - e.y) <= c.explodeRadius)
      this.hurtPlayer(e.damage, { type: 'explosion', enemyId: e.id });
    this.enemyPool.release(e);
  }

  private updateGems(dt: number): void {
    const p = this.player;
    for (const g of this.gemPool.all()) {
      if (!g.active) continue;
      const caught = updateGem(g, dt, p);
      if (g.flying && g.trailT <= 0) {
        g.trailT = BALANCE.juice.trailEvery; this.fx.gemTrail(g);
      }
      if (caught) {
        if (g.heart) this.applyHeal(BALANCE.heal.heartHeal);
        else if (g.core) this.pickCore();
        else {
          this.pendingLevels += p.addXp(g.value * this.variant.xpMul);
          if (p.lastHeal > 0)
            this.damageNumbers.spawnHeal(p.x, p.y, Math.round(p.lastHeal));
          this.emit({ type: 'pickup', x: p.x, y: p.y, amount: g.value });
        }
        this.gemPool.release(g);
      }
    }
  }

  private updateCoins(dt: number): void {
    const p = this.player;
    for (const c of this.coinPool.all()) {
      if (!c.active) continue;
      if (updateCoin(c, dt, p)) {
        this.gold += c.value;
        this.emit({ type: 'coin', x: p.x, y: p.y, amount: c.value });
        this.coinPool.release(c);
      }
    }
  }

  private takeSnapshot(dt: number, input: InputState): void {
    this.snapshotTimer -= dt;
    if (this.snapshotTimer > 0) return;
    this.snapshotTimer = BALANCE.death.snapshotEvery;
    const p = this.player;
    this.deathLog.recordSnapshot(
      this.time, p.hpRatio, p.x, p.y, input.moveX, input.moveY,
      this.enemyPool.all(), this.gemPool.all(),
    );
  }

  private openLevelUp(): void {
    this.pendingLevels--;
    this.phase = 'levelup';
    this.offers = drawOffers(this.streams.offers, this.build);
    this.emit({ type: 'levelup', x: this.player.x, y: this.player.y });
  }

  /** 回血：不超过最大生命，绿色数字 + heal 事件。 */
  private applyHeal(amount: number): void {
    const p = this.player;
    const gain = Math.min(amount, p.stats.maxHp - p.hp);
    if (gain > 0) {
      p.hp += gain;
      this.damageNumbers.spawnHeal(p.x, p.y, Math.round(gain));
      this.emit({ type: 'heal', x: p.x, y: p.y, amount: gain });
    }
  }

  private die(): void {
    this.phase = 'dead';
    this.death = this.deathLog.attribute(this.time, this.lethal);
    this.scoreBreakdown = computeScore(this, this.mode);
    this.emit({ type: 'death', x: this.player.x, y: this.player.y });
  }
}
